import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import { useTranslation } from "react-i18next";
import {
  acceptBillingOffer,
  clickBillingOffer,
  getBillingContext,
  getBillingOffers,
  getBillingInvoices,
  getBillingPlanCurrentCatalog,
  getBillingUsageSummary,
  postBillingCheckoutIntent,
  viewBillingOffer,
} from "../../../api/client";

function fmtDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("pt-BR");
}

function humanizeMetricKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function subscriptionStatusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "trialing") return "info";
  if (s === "past_due") return "warning";
  if (s === "suspended" || s === "cancelled") return "error";
  return "default";
}

function BillingField({ label, children, sx }) {
  return (
    <Stack spacing={0.35} sx={sx}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Box sx={{ minHeight: 22, display: "flex", alignItems: "center" }}>{children}</Box>
    </Stack>
  );
}

const BillingSettingsPage = () => {
  const { t } = useTranslation("app");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contextData, setContextData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [planCatalog, setPlanCatalog] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checkoutHint, setCheckoutHint] = useState("");
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [offers, setOffers] = useState([]);
  const [acceptingOfferId, setAcceptingOfferId] = useState(null);

  const metricLabel = useCallback(
    (key) => t(`billingPage.metricKeys.${key}`, { defaultValue: humanizeMetricKey(key) }),
    [t]
  );

  const billingModeLabel = useCallback(
    (mode) =>
      t(`billingPage.billingMode.${String(mode || "").toLowerCase()}`, {
        defaultValue: String(mode || "-"),
      }),
    [t]
  );

  const subscriptionStatusLabel = useCallback(
    (status) =>
      t(`billingPage.subscriptionStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const invoiceStatusLabel = useCallback(
    (status) =>
      t(`billingPage.invoiceStatus.${String(status || "").toLowerCase()}`, {
        defaultValue: String(status || "-"),
      }),
    [t]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ctx, usage] = await Promise.all([
        getBillingContext(),
        getBillingUsageSummary(),
      ]);
      let catalog = [];
      try {
        const sliceRes = await getBillingPlanCurrentCatalog();
        catalog = Array.isArray(sliceRes?.items) ? sliceRes.items : [];
      } catch {
        catalog = [];
      }
      let inv = [];
      try {
        const invRes = await getBillingInvoices();
        inv = Array.isArray(invRes?.items) ? invRes.items : [];
      } catch {
        inv = [];
      }
      let offersData = [];
      try {
        const offerRes = await getBillingOffers({ autoGenerate: 1 });
        offersData = Array.isArray(offerRes?.items) ? offerRes.items : [];
      } catch {
        offersData = [];
      }
      setContextData(ctx || null);
      setUsageData(usage || null);
      setPlanCatalog(catalog);
      setInvoices(inv);
      setOffers(offersData);
    } catch (err) {
      setError(err?.message || t("billingPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const metrics = useMemo(() => {
    const arr = usageData?.metrics;
    return Array.isArray(arr) ? arr : [];
  }, [usageData]);

  const usageByMetric = useMemo(() => {
    const map = {};
    for (const m of metrics) {
      map[m.metric_key] = Number(m.total_quantity || 0);
    }
    return map;
  }, [metrics]);

  const activePlanLimits = useMemo(() => {
    const activePlanVersionId = Number(contextData?.entitlements?.planVersionId || 0);
    if (!activePlanVersionId) return [];
    const rows = planCatalog.filter((row) => Number(row.plan_version_id) === activePlanVersionId);
    const seen = new Set();
    return rows
      .filter((r) => r.metric_key)
      .filter((r) => {
        const key = `${r.plan_version_id}:${r.metric_key}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [planCatalog, contextData?.entitlements?.planVersionId]);

  return (
    <Box sx={{ pt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} spacing={2}>
        <Box>
          <Typography variant="h4">{t("billingPage.titleWorkspace")}</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
          sx={{ flexShrink: 0 }}
        >
          {t("billingPage.refresh")}
        </Button>
      </Stack>

      {!loading ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("billingPage.infoWorkspace")}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {!loading && contextData?.domain && contextData.domain.dominioId == null ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sem vínculo em <strong>xprobase.dominios</strong> para o workspace atual. O header{" "}
          <strong>Company</strong> deve coincidir com: <strong>bd</strong> (schema MySQL, quando preenchido), com o
          host em <strong>dominio</strong> (ex.: cliente.xpro.me), com o <strong>primeiro segmento</strong> desse
          host (slug do login, ex.: cliente) ou com o <strong>id</strong> numérico do domínio.
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t("billingPage.sectionOverview")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardHeader
                title={t("billingPage.cardPlan")}
                titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, flex: 1, display: "flex", flexDirection: "column" }}>
                <Stack spacing={2} sx={{ flex: 1 }}>
                  <BillingField label={t("billingPage.fieldDomainId")}>
                    <Typography variant="body1" fontWeight={600}>
                      {contextData?.domain?.dominioId ?? "—"}
                    </Typography>
                  </BillingField>
                  <BillingField label={t("billingPage.fieldBillingMode")}>
                    <Chip
                      size="small"
                      label={billingModeLabel(contextData?.entitlements?.billingMode)}
                      variant="outlined"
                      color="primary"
                    />
                  </BillingField>
                  <BillingField label={t("billingPage.fieldSubscriptionStatus")}>
                    <Chip
                      size="small"
                      label={subscriptionStatusLabel(contextData?.entitlements?.subscriptionStatus)}
                      color={subscriptionStatusChipColor(contextData?.entitlements?.subscriptionStatus)}
                    />
                  </BillingField>
                  <BillingField label={t("billingPage.fieldPlanVersion")}>
                    <Typography variant="body1" fontWeight={600}>
                      {contextData?.entitlements?.planVersionId != null
                        ? t("billingPage.planVersionValue", {
                            version: contextData.entitlements.planVersionId,
                          })
                        : "—"}
                    </Typography>
                  </BillingField>
                  <BillingField label={t("billingPage.fieldEnforceLimits")}>
                    <Chip
                      size="small"
                      label={
                        contextData?.entitlements?.enforceLimits
                          ? t("billingPage.yes")
                          : t("billingPage.no")
                      }
                      color={contextData?.entitlements?.enforceLimits ? "success" : "default"}
                      variant="outlined"
                    />
                  </BillingField>
                  <BillingField label={t("billingPage.fieldBillingCycle")}>
                    <Typography variant="body2">
                      {fmtDate(contextData?.entitlements?.periodStart)} —{" "}
                      {fmtDate(contextData?.entitlements?.periodEnd)}
                    </Typography>
                  </BillingField>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t("billingPage.contactAdminForPlan")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {offers.length > 0 ? (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader
                  title={t("billingPage.cardOffers", { defaultValue: "Recomendações automáticas" })}
                  subheader={t("billingPage.cardOffersHint", {
                    defaultValue: "Ofertas de upgrade e adicionais calculadas conforme seu consumo.",
                  })}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={1.25}>
                    {offers.map((offer) => (
                      <Box key={offer.id} sx={{ p: 1.2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.2}>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {offer.offerType === "upgrade_plan"
                                ? t("billingPage.offerUpgradeTitle", { defaultValue: "Upgrade de plano recomendado" })
                                : t("billingPage.offerAddonTitle", { defaultValue: "Pacote adicional recomendado" })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {offer?.payload?.message ||
                                t("billingPage.offerDefaultMessage", {
                                  defaultValue: "Oferta baseada no uso recente da plataforma.",
                                })}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => viewBillingOffer(offer.id)}
                            >
                              {t("billingPage.offerViewed", { defaultValue: "Marcar como visto" })}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={acceptingOfferId != null}
                              onClick={async () => {
                                setAcceptingOfferId(offer.id);
                                setError("");
                                setCheckoutHint("");
                                try {
                                  await clickBillingOffer(offer.id);
                                  const r = await acceptBillingOffer(offer.id);
                                  if (r?.checkout?.url) {
                                    window.open(r.checkout.url, "_blank", "noopener,noreferrer");
                                  }
                                  setCheckoutHint(r?.message || t("billingPage.offerAccepted", { defaultValue: "Oferta contratada." }));
                                  await loadData();
                                } catch (err) {
                                  setError(err?.message || t("billingPage.offerAcceptError", { defaultValue: "Não foi possível contratar a oferta." }));
                                } finally {
                                  setAcceptingOfferId(null);
                                }
                              }}
                            >
                              {acceptingOfferId === offer.id
                                ? t("billingPage.offerHiring", { defaultValue: "Contratando..." })
                                : t("billingPage.offerHireNow", { defaultValue: "Contratar com 1 clique" })}
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ) : null}

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardHeader
                avatar={<ReceiptLongOutlinedIcon color="action" />}
                title={t("billingPage.cardInvoices")}
                titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, flex: 1 }}>
                {invoices.length === 0 ? (
                  <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 3, px: 1 }}>
                    <ReceiptLongOutlinedIcon sx={{ fontSize: 40, opacity: 0.35 }} />
                    <Typography variant="subtitle2">{t("billingPage.invoicesEmptyTitle")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("billingPage.invoicesEmptyHint")}
                    </Typography>
                  </Stack>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
                    {invoices.map((inv) => (
                      <Box
                        component="li"
                        key={inv.id}
                        sx={{
                          py: 1.25,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={600}>
                              {inv.number_code || `#${inv.id}`}
                            </Typography>
                            <Chip
                              size="small"
                              label={invoiceStatusLabel(inv.status)}
                              variant="outlined"
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {(Number(inv.total_cents || 0) / 100).toFixed(2)}{" "}
                            {inv.currency_code || "BRL"}
                          </Typography>
                          {String(inv.status).toLowerCase() === "pending" ? (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={payingInvoiceId != null}
                              onClick={async () => {
                                setPayingInvoiceId(inv.id);
                                setCheckoutHint("");
                                setError("");
                                try {
                                  const r = await postBillingCheckoutIntent({
                                    invoiceId: inv.id,
                                    provider: "mercadopago",
                                  });
                                  if (r?.url) {
                                    window.open(r.url, "_blank", "noopener,noreferrer");
                                    setCheckoutHint(t("billingPage.checkoutOpenedInTab"));
                                  } else {
                                    setCheckoutHint(r?.message || t("billingPage.checkoutNoUrl"));
                                  }
                                } catch (err) {
                                  setError(err?.message || t("billingPage.checkoutError"));
                                } finally {
                                  setPayingInvoiceId(null);
                                }
                              }}
                            >
                              {payingInvoiceId === inv.id
                                ? t("billingPage.checkoutOpening")
                                : t("billingPage.payMercadoPago")}
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                )}
                {checkoutHint ? (
                  <Typography variant="caption" display="block" sx={{ mt: 1.5 }} color="text.secondary">
                    {checkoutHint}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardHeader
                avatar={<BarChartOutlinedIcon color="action" />}
                title={t("billingPage.cardUsage")}
                titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
                subheader={t("billingPage.usagePeriod", {
                  from: fmtDate(usageData?.period?.from),
                  to: fmtDate(usageData?.period?.to),
                })}
                subheaderTypographyProps={{ variant: "body2" }}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, flex: 1 }}>
                {metrics.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t("billingPage.usageEmpty")}
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {metrics.map((m) => (
                      <Tooltip key={`${m.metric_key}-${m.total_quantity}`} title={m.metric_key} arrow>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                          sx={{
                            py: 0.75,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {metricLabel(m.metric_key)}
                          </Typography>
                          <Typography variant="h6" component="span" fontWeight={700}>
                            {m.total_quantity}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={t("billingPage.cardLimits")}
                titleTypographyProps={{ variant: "subtitle1", fontWeight: 700 }}
                subheader={t("billingPage.cardLimitsHint")}
                subheaderTypographyProps={{ variant: "body2", color: "text.secondary" }}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1 }}>
                {activePlanLimits.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    {t("billingPage.limitsEmpty")}
                  </Typography>
                ) : (
                  <Stack spacing={2.5}>
                    {activePlanLimits.map((limitRow) => {
                      const metricKey = String(limitRow.metric_key);
                      const current = Number(usageByMetric[metricKey] || 0);
                      const max = Number(limitRow.limit_value || 0);
                      const isUnlimited = String(limitRow.limit_type) === "unlimited";
                      const isDisabled = String(limitRow.limit_type) === "disabled";
                      const percent = !isUnlimited && !isDisabled && max > 0
                        ? Math.min(100, Math.round((current / max) * 100))
                        : 0;
                      return (
                        <Box key={`${limitRow.plan_version_id}-${metricKey}`}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            spacing={2}
                            sx={{ mb: 1 }}
                          >
                            <Tooltip title={metricKey} arrow>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {metricLabel(metricKey)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                  {metricKey}
                                </Typography>
                              </Box>
                            </Tooltip>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {isDisabled
                                ? t("billingPage.limitDisabled")
                                : isUnlimited
                                ? t("billingPage.limitUnlimited")
                                : t("billingPage.limitFraction", { current, max })}
                            </Typography>
                          </Stack>
                          {!isUnlimited && !isDisabled ? (
                            <Box>
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                color={percent >= 90 ? "error" : percent >= 70 ? "warning" : "primary"}
                                sx={{
                                  height: 10,
                                  borderRadius: 5,
                                  bgcolor: "action.hover",
                                  "& .MuiLinearProgress-bar": { borderRadius: 5 },
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                                {t("billingPage.limitPercentUsed", { percent })}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BillingSettingsPage;
